import React, { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Button from '../../components/common/button/Button';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import styles from './CustomerBalanceDetail.module.scss';
// import settingsService from '../../services/settingsService';
import { toast } from 'react-toastify';
import reportService from '../../services/reportService';
import { BalanceReportDetail } from '../../types/reportTypes';
import companyService from '../../services/companyService';
import { Company } from '../../types/companyTypes';

const CustomerBalanceDetail: React.FC <{ role?: string }> = ({ role = 'admin' }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [balanceDetail, setBalanceDetail] = useState<BalanceReportDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: 'Invoice Management', address: '' });
  const [searchTerm, setSearchTerm] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  // Get date range from URL params
  const startDate = searchParams.get('startDate') || undefined;
  const endDate = searchParams.get('endDate') || undefined;
  const [company, setCompany] = useState<Company | null>(null);
  

 const fetchCompany = async () => {
     try {
       const response = await companyService.getCompany('1'); // Replace with actual company ID
       
       if (response && typeof response === 'object') {
         const normalizedCompany: Company = {
           id: response.id ?? '',
           name: response.name ?? '',
           email: response.email ?? '',
           phone: response.phone ?? '',
           address: response.address ?? '',
           city: response.city ?? '',
           state: response.state ?? '',
           zip_code: response.zip_code ?? '',
           country: response.country ?? '',
           website: response.website ?? '',
           gst: response.gst ?? '',
           is_active: response.is_active ?? true,
           created_at: response.created_at ?? new Date().toISOString(),
           updated_at: response.updated_at ?? new Date().toISOString(),
         };
 
         setCompany(normalizedCompany);
         
       }
     } catch (err) {
       console.error('Error fetching company:', err);
       toast.error('Failed to fetch company details');
     } finally {
       setLoading(false);
     }
   };
 
   useEffect(() => {
     fetchCompany();
   }, []);

  useEffect(() => {
    const loadBalanceDetail = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await reportService.getBalanceReportDetail(id, { startDate, endDate });
        setBalanceDetail(data);
      } catch (error) {
        console.error('Failed to load balance detail:', error);
        toast.error('Failed to load customer balance detail');
      } finally {
        setLoading(false);
      }
    };

    loadBalanceDetail();
  }, [id, startDate, endDate]);

  const filteredTransactions = balanceDetail?.transactions.filter((transaction) => {
    const search = searchTerm.toLowerCase();
    const matchesInvoice = transaction.invoice_number?.toLowerCase().includes(search);
    const matchesNotes = transaction.notes?.toLowerCase().includes(search);
    
    // If invoice, also search in items
    let matchesItems = false;
    if (transaction.type === 'invoice' && transaction.items) {
      matchesItems = transaction.items.some(item => 
        item.description?.toLowerCase().includes(search) ||
        item.account_head_name?.toLowerCase().includes(search)
      );
    }
    
    return matchesInvoice || matchesNotes || matchesItems;
  }) || [];

  const getCustomerPrintContent = () => {
    if (!balanceDetail) return '';
    const now = new Date();
  const printDate = now.toLocaleDateString();
  const printTime = now.toLocaleTimeString();

  const companyName = company?.name && company.name.length > 0 ? company.name : 'Company Name';
  const companyAddressParts = [
    company?.address,
    company?.city,
    company?.state,
    company?.zip_code,
    company?.country,
  ].filter(Boolean); // only include non-empty parts
  const companyAddress = companyAddressParts.join(', ');

    return `
      <div style="text-align:center; margin-bottom:8px;">
      <h1 style="margin-bottom:0; font-size:1.4rem; color:green;">
        ${companyName}
      </h1>
      ${companyAddress ? `<div style="font-size:1rem; margin-bottom:4px;">${companyAddress}</div>` : ''}
      ${company?.phone ? `<div>Contact: ${company.phone}</div>` : ''}
      ${company?.email ? `<div>Email: ${company.email}</div>` : ''}
      ${company?.website ? `<div>Website: ${company.website}</div>` : ''}
      ${company?.gst ? `<div>GST: ${company.gst}</div>` : ''}
      <div style="margin-top:4px; font-size:0.9rem;">Printed on ${printDate} - ${printTime}</div>
    </div>
      <div style="display:flex; justify-content:space-between; font-weight:600; margin-bottom:8px;">
        <div>Customer Name: ${balanceDetail.customer.name}</div>
      </div>
      <div style="margin-bottom:4px;">
      <div><strong>Customer Phone:</strong> ${balanceDetail.customer.phone}</div>
        <div><strong>Customer Email:</strong> ${balanceDetail.customer.email || '-'}</div>
        <div><strong>Customer Address:</strong> ${balanceDetail.customer.address || '-'}</div>
      </div>
      <table style="width:100%; border-collapse:collapse; margin-bottom:8px;">
        <thead>
          <tr>
            <th style="border:1px solid #ccc; padding:6px;">S.No</th>
            <th style="border:1px solid #ccc; padding:6px;">Date</th>
            <th style="border:1px solid #ccc; padding:6px;">Type</th>
            <th style="border:1px solid #ccc; padding:6px;">Description</th>
            <th style="border:1px solid #ccc; padding:6px;">Invoice Amount</th>
            <th style="border:1px solid #ccc; padding:6px;">Payment Amount</th>
            <th style="border:1px solid #ccc; padding:6px;">Running Balance</th>
          </tr>
        </thead>
        <tbody>
          ${balanceDetail.transactions.map((t, idx) => {
            const bgStyle = t.type === 'invoice' 
              ? 'background:#f0f8ff !important; color:#000 !important; -webkit-print-color-adjust: exact; color-adjust: exact; print-color-adjust: exact;'
              : '';
            
            const baseRow = `
              <tr style="${bgStyle}">
                <td style="border:1px solid #ccc; padding:6px;">${idx + 1}</td>
                <td style="border:1px solid #ccc; padding:6px;">${formatDate(t.date)}</td>
                <td style="border:1px solid #ccc; padding:6px;">${t.type === 'invoice' ? 'Invoice' : 'Payment'}</td>
                <td style="border:1px solid #ccc; padding:6px;">${t.invoice_number || t.notes || '-'}</td>
                <td style="border:1px solid #ccc; padding:6px;">${t.invoice_amount.toLocaleString()}</td>
                <td style="border:1px solid #ccc; padding:6px;">${t.payment_amount.toLocaleString()}</td>
                <td style="border:1px solid #ccc; padding:6px;"><strong>${t.running_balance.toLocaleString()}</strong></td>
              </tr>
            `;
            
            // Add item details for invoices
            if (t.type === 'invoice' && t.items && t.items.length > 0) {
              const itemRows = t.items.map(item => `
                <tr style="background:#f9fafb;">
                  <td style="border:1px solid #ccc; padding:6px;"></td>
                  <td style="border:1px solid #ccc; padding:6px;"></td>
                  <td style="border:1px solid #ccc; padding:6px;">${item.account_head_name || '-'}</td>
                  <td style="border:1px solid #ccc; padding:6px; padding-left:20px;">• ${item.description || '-'}</td>
                  <td style="border:1px solid #ccc; padding:6px;">${item.quantity} × ${item.unit_price.toLocaleString()}</td>
                  <td style="border:1px solid #ccc; padding:6px;">${((Number(item.amount) || 0) + (Number(item.service_charge) || 0)).toLocaleString()}</td>
                  <td style="border:1px solid #ccc; padding:6px;"></td>
                </tr>
              `).join('');
              return baseRow + itemRows;
            }
            
            return baseRow;
          }).join('')}
          ${balanceDetail.transactions.length > 0 ? `
            <tr style="font-weight:bold; background:#f5f5f5;">
              <td colspan="4" style="text-align:right; border:1px solid #ccc; padding:6px;">Total</td>
              <td style="border:1px solid #ccc; padding:6px;">${balanceDetail.summary.total_invoice_amount.toLocaleString()}</td>
              <td style="border:1px solid #ccc; padding:6px;">${balanceDetail.summary.total_payment_amount.toLocaleString()}</td>
              <td style="border:1px solid #ccc; padding:6px;">${balanceDetail.summary.current_balance.toLocaleString()}</td>
            </tr>
          ` : ''}
        </tbody>
      </table>
    `;
  };

  const handleCustomerPrint = () => {
    const printWindow = window.open('', '', 'height=800,width=1100');
    if (printWindow) {
      printWindow.document.write(`<html><head><title>${balanceDetail?.customer.name} Balance Statement</title>`);
      printWindow.document.write(`
        <style>
          body { font-family: Arial, sans-serif; margin: 10px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; }
          th { background: #f5f5f5; }
          @media print {
            body { margin: 10px; }
            @page { 
              margin: 0.5cm;
              size: auto;
            }
          }
        </style>
      `);
      printWindow.document.write('</head><body>');
      printWindow.document.write(getCustomerPrintContent());
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading balance detail...</p>
      </div>
    );
  }

  if (!balanceDetail) {
    return (
      <div className={styles.errorContainer}>
        <h2>Balance Detail Not Found</h2>
        <p>Unable to load customer balance information.</p>
        <Button onClick={() => navigate('/reports')}>
          Back to Reports
        </Button>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

   const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
        }).format(amount);

      };

  return (
    <div className={styles.customerDetail} ref={contentRef}>
      <div className={styles.printHeader}>
        <h2>{balanceDetail.customer.name}</h2>
        <p>Current Balance: {formatCurrency(balanceDetail.summary.current_balance)}</p>
      </div>

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <button
            className={styles.collapseToggle}
            onClick={() => navigate(-1)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          
          <h2>{balanceDetail.customer.name} - Balance Report</h2>
          {/* <div className={styles.meta}>
            <span className={`${styles.status} ${balanceDetail.summary.current_balance > 0 ? styles.overdue : styles.paid}`}>
              Balance: {formatCurrency(balanceDetail.summary.current_balance)}
            </span>
          </div> */}
        </div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={handleCustomerPrint}
            disabled={isGeneratingPDF}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </Button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.section}>
          <h3>Customer Information</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>Customer Name</label>
              <span>{balanceDetail.customer.name}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Phone Number</label>
              <span>{balanceDetail.customer.phone}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Email Address</label>
              <span>{balanceDetail.customer.email || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Address</label>
              <span>{balanceDetail.customer.address || '-'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>GST Number</label>
              <span>{balanceDetail.customer.gst || '-'}</span>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3>Summary</h3>
          <div className={styles.infoGrid}>
            {/* <div className={styles.infoItem}>
              <label>Date Range</label>
              <span>{balanceDetail.summary.date_range.from} to {balanceDetail.summary.date_range.to}</span>
            </div> */}
            <div className={styles.infoItem}>
              <label>Total Invoices</label>
              <span>{balanceDetail.summary.total_invoices}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Total Payments</label>
              <span>{balanceDetail.summary.total_payments}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Total Invoice Amount</label>
              <span>{formatCurrency(balanceDetail.summary.total_invoice_amount)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Total Payment Amount</label>
              <span>{formatCurrency(balanceDetail.summary.total_payment_amount)}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Current Balance</label>
              <span className={balanceDetail.summary.current_balance > 0 ? styles.balanceOwed : styles.balancePaid}>
                {formatCurrency(balanceDetail.summary.current_balance)}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3>Transaction History</h3>
          {/* <div style={{ marginBottom: "12px" }}>
            <input
              type="text"
              placeholder="Search by invoice, notes or item description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                width: "100%",
                maxWidth: "400px",
              }}
            />
          </div> */}
          {filteredTransactions.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No transactions found.</p>
            </div>
          ) : (
            <div className={styles.invoiceTable}>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Invoice Amount</th>
                    <th>Payment Amount</th>
                    <th>Running Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => (
                    <React.Fragment key={transaction.id}>
                      <tr className={transaction.type === 'payment' ? styles.receivedRow : styles.invoiceRow}>
                        <td>{formatDate(transaction.date)}</td>
                        <td>
                          <span className={`${styles.typeBadge} ${styles[transaction.type]}`}>
                            {transaction.type === 'invoice' ? 'Invoice' : 'Payment'}
                          </span>
                        </td>
                        <td>
                          {transaction.type === 'invoice' 
                            ? transaction.invoice_number 
                            : transaction.notes || transaction.payment_method?.toUpperCase() || '-'
                          }
                        </td>
                        <td className={styles.invoiceAmount}>
                          {transaction.invoice_amount > 0 ? formatCurrency(transaction.invoice_amount) : '-'}
                        </td>
                        <td className={styles.receivedAmount}>
                          {transaction.payment_amount > 0 ? formatCurrency(transaction.payment_amount) : '-'}
                        </td>
                        <td className={styles.runningBalance}>
                          <strong>{formatCurrency(transaction.running_balance)}</strong>
                        </td>
                      </tr>
                      {/* Show invoice items if this is an invoice and items are available */}
                      {transaction.type === 'invoice' && transaction.items && transaction.items.length > 0 && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0, background: '#f9fafb' }}>
                            <table style={{ width: '100%', margin: 0, background: '#f9fafb' }}>
                              <thead>
                                <tr>
                                  <th style={{ fontWeight: 500, fontSize: '0.8rem' }}>Product Name</th>
                                  <th style={{ fontWeight: 500, fontSize: '0.8rem' }}>Quantity</th>
                                  <th style={{ fontWeight: 500, fontSize: '0.8rem' }}>Unit Price</th>
                                  <th style={{ fontWeight: 500, fontSize: '0.8rem' }}>Item Total Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {transaction.items.map((item, idx) => (
                                  <tr key={idx}>
                                    <td style={{ fontSize: '0.8rem' }}>{item.description || '-'}</td>
                                    <td style={{ fontSize: '0.8rem' }}>{item.quantity}</td>
                                    <td style={{ fontSize: '0.8rem' }}>{formatCurrency(item.unit_price)}</td>
                                    <td style={{ fontSize: '0.8rem' }}>
                                      {formatCurrency((Number(item.amount) || 0) + (Number(item.service_charge) || 0))}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  <tr className={styles.totalRow}>
                    <td colSpan={3} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</td>
                    <td className={styles.invoiceAmount}>
                      <strong>{formatCurrency(balanceDetail.summary.total_invoice_amount)}</strong>
                    </td>
                    <td className={styles.receivedAmount}>
                      <strong>{formatCurrency(balanceDetail.summary.total_payment_amount)}</strong>
                    </td>
                    <td className={styles.runningBalance}>
                      <strong>{formatCurrency(balanceDetail.summary.current_balance)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerBalanceDetail;